import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { UniSlider, UniSwitch } from '@/design-system/components/inputs';
import { Slider } from '@/design-system/components/slider/Slider';
import { ModalHeader } from '@/design-system/components/modal';
import { UniNotes } from '@/design-system/components/notes/UniNote';
import { H5, TextBody, TextCaption } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _, { debounce } from 'lodash';
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { thousandSeparator } from '@/utils/numberFormatting';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SetCardLimitStackParamList, SetCardLimitStackRoutes } from './type';
import { UniInputAmount } from '@/design-system/components/inputs';
import { CardWithLimitProp } from '@/features/account-management/types';
import { CardLimitSubtitle, CardLimitTitle } from '../../options';
import { CardLimitType } from '../../enum';

export interface SetCardLimitProps {
  onClose: () => void;
  onGoBack: () => void;
  onLayout: (event: any) => void;
  dailyLimitType: string | undefined;
  onData?: (data: any) => void;
  account: string;
  tab?: number;
  record?: CardWithLimitProp;
  func?: (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const DailyPurchaseLimitScreen: React.FC<SetCardLimitProps> = (props) => {
  const { onClose, onGoBack, onLayout, dailyLimitType, onData, account, tab, record, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const {
    topHeader,
    body,
    textMb,
    flexContainer,
    textCaption,
    amount,
    dailyLimitBox,
    limit,
    flex,
    buttonBox,
    noteContainer,
    title,
    amountDisabled
  } = sxStyles;

  const initialValue = () => {
    switch (dailyLimitType) {
      case CardLimitType.DailyPurchaseLimit:
        return record?.dailyPurchaseLimit;
      case CardLimitType.AtmLimit:
        return record?.dailyAtmLimit;
      case CardLimitType.DailyLimit:
        return record?.dailyLimit;
      case CardLimitType.SingleTransactionLimit:
        return record?.singleTransactionLimit;
      default:
        return null;
    }
  };

  const [value, setValue] = useState<number>(initialValue() ?? record?.cardLimit ?? 0);

  const [maxValue, setMaxValue] = useState<number>(record?.limit || 0);
  const [isEnabled, setIsEnabled] = useState(initialValue() == null);

  const navigation = useNavigation<NativeStackNavigationProp<SetCardLimitStackParamList>>();

  const debounceValue = useCallback((value) => setInputValue(value), []);
  const setInputValue = debounce((value) => {
    setValue(value), 300;
  });

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <ModalHeader
          title={CardLimitTitle(t).find((e) => e.value == dailyLimitType)?.label}
          closeModal={() => navigation.getParent()?.goBack()}
        />
      </View>
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {CardLimitSubtitle(t).find((e) => e.value == dailyLimitType)?.label}
        </TextBody>
        <View sx={flexContainer}>
          <View sx={textCaption}>
            <TextCaption semiBold>{t('card-account-management::set-card-limit.text--use-entire-secured-credit-limit')}</TextCaption>
            <TextCaption>{t('card-account-management::set-card-limit.text--choose-entire-secured-limit')}</TextCaption>
            <TextCaption semiBold sx={{ color: '#F5423E' }}>
              {t('card-account-management::set-card-limit.text--please-use-with-caution')}
            </TextCaption>
          </View>
          <UniSwitch
            value={isEnabled}
            onValueChange={(value) => {
              setIsEnabled(value);
            }}
          />
        </View>
        {!isEnabled && (
          <>
            <View sx={dailyLimitBox}>
              <View sx={{ ...limit, alignItems: 'flex-end' }}>
                <TextBody sx={{ pb: '$2' }}>{record?.currency || 'HKD'}</TextBody>
                <UniInputAmount
                  sx={{ width: '42%' }}
                  assetPrefix={false}
                  maxFlag={false}
                  editable={!isEnabled}
                  sxWrap={{ border: 0 }}
                  sxInput={{
                    fontWeight: 'bolder',
                    fontSize: '$5',
                    color: isEnabled ? '$navbarTextInactive' : '$secondary',
                    ml: '$2',
                    pl: 0
                  }}
                  asset={''}
                  value={value}
                  max={maxValue}
                  onValueChange={(v) => v && debounceValue(v)}
                />
              </View>
              <Divider width={250} sx={{ margin: 'auto', backgroundColor: '$secondary' }} />
              <TextCaption semiBold>{t('card-account-management::set-card-limit.text--daily-limit')}</TextCaption>
            </View>
            <Slider
              value={value >= maxValue ? maxValue : value}
              minValue={0}
              maxValue={maxValue}
              step={100}
              labelFormatter={(value) => ''}
              onValueChange={setValue}
              disabled={isEnabled}
            />
            <View sx={flex}>
              <TextBody semiBold>0 {record?.currency || 'HKD'}</TextBody>
              <TextBody semiBold>
                {thousandSeparator({ value: maxValue })} {record?.currency || 'HKD'}
              </TextBody>
            </View>
          </>
        )}
        {dailyLimitType == CardLimitType.AtmLimit && (
          <View sx={noteContainer}>
            <UniNotes
              title={t('card-account-management::set-card-limit.text--note')}
              paragraphs={[t('card-account-management::set-card-limit.text--service-fee-per-transaction')]}
              sx={title}
            />
          </View>
        )}
        <View sx={buttonBox}>
          <UniButton
            variant={ButtonVariant.outline1}
            size="medium"
            sx={{ width: 216, height: 44 }}
            label={t('card-account-management::set-card-limit.button--back')}
            onPress={() => navigation.replace(SetCardLimitStackRoutes.Main, { onLayout, account, tab })}
          />
          <UniButton
            size="medium"
            sx={{ width: 216, height: 44 }}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::set-card-limit.button--set-limit')}
            onPress={() => {
              onData && onData({ value, isEnabled });
              navigation.navigate(SetCardLimitStackRoutes.Confirmation, {
                onLayout: onLayout,
                data: { value, isEnabled },
                dailyLimitType: dailyLimitType,
                account,
                tab,
                record,
                func,
                onReturnData,
                refresh
              });
            }}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    height: 70,
    justifyContent: 'center'
  },
  body: {
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  flexContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '$backgroundSecondary',
    borderRadius: 10,
    padding: 16
  },
  textCaption: {
    width: '77%',
    gap: 4
  },
  amount: {
    color: '$secondary',
    ml: '$2'
  },
  amountDisabled: {
    color: '$navbarTextInactive',
    ml: '$2'
  },
  limit: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dailyLimitBox: {
    alignItems: 'center',
    py: '$5',
    mt: '$5',
    gap: 4
  },
  flex: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonBox: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    mt: '$10'
  },
  noteContainer: {
    mt: '$5',
    color: '$textSecondary',
    gap: 4
  },
  title: {
    color: '$textSecondary',
    fontSize: 12
  }
};
